import { useState, useEffect } from 'react';
import { supabase } from '~/services/supabase';

export interface IAttioAttribute {
  api_slug: string;
  title: string;
  type: string;
  is_writable: boolean;
}

interface UseAttioAttributesResult {
  personAttributes: IAttioAttribute[];
  companyAttributes: IAttioAttribute[];
  loading: boolean;
  error: string | null;
}

export const useAttioAttributes = (enabled: boolean): UseAttioAttributesResult => {
  const [personAttributes, setPersonAttributes] = useState<IAttioAttribute[]>([]);
  const [companyAttributes, setCompanyAttributes] = useState<IAttioAttribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchAttributes = async () => {
      try {
        const [personResult, companyResult] = await Promise.all([
          supabase.functions.invoke('attio-attributes', {
            body: { action: 'listPersonAttributes' },
          }),
          supabase.functions.invoke('attio-attributes', {
            body: { action: 'listCompanyAttributes' },
          }),
        ]);

        if (cancelled) return;

        if (personResult.error) throw personResult.error;
        if (companyResult.error) throw companyResult.error;

        setPersonAttributes(personResult.data.attributes ?? []);
        setCompanyAttributes(companyResult.data.attributes ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch attributes');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAttributes();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { personAttributes, companyAttributes, loading, error };
};
